import { useEffect, useMemo, useState } from "react";
import { usePathname, navigate } from "../../lib/router";
import { api } from "../../api";
import { Helmet } from "react-helmet-async";
import type { BlogPost } from "../../types";
import { Button } from "../../components/ui/button";
import { fixImageUrl } from "../../lib/utils";
import Navbar from "../../components/Navbar";

export function PostPage() {
  const path = usePathname();
  const slug = path.split("/").pop()!;
  const [post, setPost] = useState<BlogPost | null>(null);
  const [allPosts, setAllPosts] = useState<BlogPost[] | null>(null);
  useEffect(() => {
    (async () => {
      const res = await api.getBlogPost(slug);
      if (res.ok) setPost(res.data);
    })();
  }, [slug]);
  useEffect(() => {
    (async () => {
      const res = await api.listBlogPosts();
      if (res.ok) setAllPosts(res.data);
    })();
  }, []);
  const related = useMemo(() => {
    if (!allPosts) return [] as BlogPost[];
    const top4 = allPosts.slice(0, 4);
    const withoutCurrent = top4.filter(p => p.slug !== slug);
    return withoutCurrent.slice(0, 3);
  }, [allPosts, slug]);
    if (!post) return <div className="max-w-screen-lg mx-auto px-4">Carregando...</div>;
    return (
      <div className="min-h-screen w-full flex flex-col bg-white text-neutral-900">
        <Helmet>
          <title>{post.meta_title || post.title}</title>
        {post.meta_description && (
          <meta name="description" content={post.meta_description} />
        )}
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content={post.meta_title || post.title}
        />
        {post.meta_description && (
          <meta property="og:description" content={post.meta_description} />
        )}
        <meta
          property="og:url"
          content={typeof window !== "undefined" ? window.location.href : ""}
        />
        {post.meta_site_name && (
          <meta property="og:site_name" content={post.meta_site_name} />
        )}
        <meta property="article:published_time" content={post.created_at} />
        {post.updated_at && (
          <meta property="article:modified_time" content={post.updated_at} />
        )}
        {post.meta_image && (
          <>
            <meta property="og:image" content={fixImageUrl(post.meta_image)} />
            {post.meta_image_width && (
              <meta
                property="og:image:width"
                content={String(post.meta_image_width)}
              />
            )}
            {post.meta_image_height && (
              <meta
                property="og:image:height"
                content={String(post.meta_image_height)}
              />
            )}
            {post.meta_image_type && (
              <meta property="og:image:type" content={post.meta_image_type} />
            )}
          </>
        )}
        </Helmet>
        <Navbar />

        <div className="max-w-screen-lg mx-auto flex-1 px-4 pb-16">

      <div className="mt-6 text-lg text-gray-700 space-y-6">
        <h1 className="text-4xl font-extrabold text-gray-900">{post.title}</h1>
        {post.subtitle && (
          <h2 className="mt-2 text-xl text-gray-600 italic">{post.subtitle}</h2>
        )}
      </div>

      {post.meta_image && (
        <div className="my-6">
          <img
            src={fixImageUrl(post.meta_image)}
            alt={post.meta_title || post.title}
            className="w-full rounded-xl shadow-lg"
          />
        </div>
      )}

      <div className="mt-6 text-lg text-gray-700 space-y-6">
        {post.content.map((block, idx) => {
          if (block.type === "text")
            return (
              <p
                key={idx}
                className="leading-relaxed"
                dangerouslySetInnerHTML={{ __html: block.text }}
              />
            );
          if (block.type === "image")
            return (
              <div key={idx} className="my-6">
                <img
                  src={fixImageUrl(block.url)}
                  alt={block.alt || ""}
                  className="w-full rounded-xl shadow-md"
                />
              </div>
            );
          const Tag = `h${block.level}` as keyof JSX.IntrinsicElements;
          return (
            <Tag key={idx} className="text-2xl font-semibold text-gray-800">
              {block.text}
            </Tag>
          );
        })}
        </div>

        <div className="mt-12 bg-[#71B27B]/20 p-6 rounded-lg text-center">
          <h3 className="text-3xl font-semibold text-gray-800">
            Use nossa plataforma de agenda gratuitamente!
          </h3>
          <p className="mt-4 text-lg text-gray-700">
            Aproveite os recursos incríveis para gerenciar seu tempo de forma eficiente.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate("/")}
            className="mt-6 px-6 py-3 text-white bg-[#71B27B] hover:bg-[#71B27B]/90 rounded-lg"
          >
            Usar Agora
          </Button>
        </div>
        {related.length > 0 && (
          <div className="mt-16">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Mais posts</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <button
                  key={p.slug}
                  onClick={() => {
                    if (typeof window !== "undefined") window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                    navigate(`/blog/${p.slug}`);
                  }}
                  className="text-left rounded-lg border border-gray-200 hover:border-gray-300 transition p-4 bg-white"
                >
                  {p.meta_image && (
                    <img
                      src={fixImageUrl(p.meta_image)}
                      alt={p.meta_title || p.title}
                      className="w-full h-40 object-cover rounded-md mb-3"
                    />
                  )}
                  <div className="space-y-1">
                    <h4 className="text-lg font-semibold text-gray-900 line-clamp-2">{p.title}</h4>
                    {p.subtitle && (
                      <p className="text-sm text-gray-600 line-clamp-2">{p.subtitle}</p>
                    )}
                    <Button
                      variant="primary"
                      size="micro"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (typeof window !== "undefined") window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                        navigate(`/blog/${p.slug}`);
                      }}
                      className="mt-3"
                      aria-label={`Ler agora: ${p.title}`}
                    >
                      Ler agora
                    </Button>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
    );
  }

export default PostPage;
